import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Coins, Sparkles, Check, Loader2, Bitcoin } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CRYPTO_MIN_USD = 12; // packages below this are card-only (crypto network minimum)

export default function Wallet() {
  const { refreshUser } = useAuth();
  const [packages, setPackages] = useState([]);
  const [coins, setCoins] = useState(0);
  const [buying, setBuying] = useState(null);
  const [buyingCrypto, setBuyingCrypto] = useState(null);
  const [orders, setOrders] = useState([]);
  const [meta, setMeta] = useState({});
  const [boost, setBoost] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/coins/packages").then((r) => {
      setPackages(r.data.packages || []); setCoins(r.data.coins || 0);
      setMeta({ first: r.data.first_purchase, firstPct: r.data.first_bonus_pct || 20, cryptoPct: r.data.crypto_bonus_pct || 10 });
    }).catch(() => {});
    api.get("/coins/orders").then((r) => setOrders(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/site-config").then((r) => { if (r.data.crypto_bonus_boost) setBoost(r.data); }).catch(() => {}); }, []);
  useEffect(() => {
    if (!boost) return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [boost]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("Empire Coins added to your wallet! 💰");
          await refreshUser(); load(); setParams({}, { replace: true });
          return;
        }
      } catch (e) { /* ignore */ }
      if (tries++ < 6) setTimeout(poll, 1500);
      else setParams({}, { replace: true });
    };
    poll();
  }, [params, refreshUser, load, setParams]);

  // Handle return from crypto checkout (NOWPayments) — credit happens via webhook
  useEffect(() => {
    const order = params.get("crypto_order");
    if (!order) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/coins/crypto/status/${order}`);
        if (data.payment_status === "paid") {
          toast.success("Crypto payment confirmed — Empire Coins added! 💰");
          await refreshUser(); load(); setParams({}, { replace: true });
          return;
        }
      } catch (e) { /* ignore */ }
      if (tries++ < 20) setTimeout(poll, 4000);
      else { toast.info("Your crypto payment is still confirming — coins will appear once it's done."); setParams({}, { replace: true }); }
    };
    poll();
  }, [params, refreshUser, load, setParams]);

  const buy = async (pkg) => {
    setBuying(pkg.id);
    try {
      const { data } = await api.post("/coins/checkout", { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Could not start checkout — try again");
      setBuying(null);
    }
  };

  const buyCrypto = async (pkg) => {
    setBuyingCrypto(pkg.id);
    try {
      const { data } = await api.post("/coins/crypto/checkout", { package_id: pkg.id, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start crypto checkout — try again");
      setBuyingCrypto(null);
    }
  };

  return (
    <div className="p-6 md:p-12 max-w-4xl mx-auto" data-testid="wallet-page">
      <div className="overline mb-1">Your Wallet</div>
      <h2 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3"><Coins className="text-[#D4AF37]" /> Buy Empire Coins</h2>
      <p className="text-[#A0A0A0] text-sm mb-6 max-w-xl">Spend coins on virtual gifts, tips and spotlight boosts across the Empire.</p>

      <div data-testid="wallet-balance" className="pe-card rounded-2xl p-6 mb-8 flex items-center gap-4">
        <div className="w-14 h-14 rounded-full flex items-center justify-center bg-[#D4AF37]/15 border border-[#D4AF37]/40"><Coins className="text-[#D4AF37]" size={26} /></div>
        <div>
          <div className="text-xs text-[#A0A0A0]">Current balance</div>
          <div className="font-heading text-4xl gold-text">{coins.toLocaleString()}</div>
        </div>
      </div>

      {boost && (() => {
        const ends = boost.crypto_bonus_boost_ends_at ? new Date(boost.crypto_bonus_boost_ends_at).getTime() : null;
        const ms = ends ? ends - nowTs : null;
        if (ends && ms <= 0) return null;
        let cd = null;
        if (ms != null) {
          const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
          cd = `${h}h ${m}m ${s}s`;
        }
        return (
          <div data-testid="boost-banner" className="rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-2 border border-[#2ecc71]/40"
            style={{ background: "linear-gradient(100deg,#0c1a10,#0A0A0A)" }}>
            <span className="text-lg">🎁</span>
            <span className="text-sm text-white font-medium">Double-Bonus is LIVE — <span className="text-[#2ecc71]">+{Math.round((boost.crypto_bonus_boost_pct || 0.2) * 100)}% crypto bonus</span></span>
            {cd && <span className="text-sm text-[#D4AF37] ml-auto font-medium">ends in ⏳ {cd}</span>}
          </div>
        );
      })()}

      {meta.first && (
        <div data-testid="first-purchase-banner" className="rounded-2xl p-4 mb-6 flex items-center gap-3 border border-[#D4AF37]/40"
          style={{ background: "linear-gradient(100deg,#1c1608,#0A0A0A)" }}>
          <Sparkles className="text-[#D4AF37] flex-shrink-0" size={20} />
          <div className="text-sm text-white">Welcome gift 🎉 — get <span className="text-[#D4AF37] font-semibold">+{meta.firstPct}% bonus coins</span> on your very first top-up (any package).</div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {packages.map((p, i) => (
          <motion.div key={p.id} data-testid={`coin-package-${p.id}`}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            className={`pe-card rounded-2xl p-5 flex flex-col ${p.badge ? "border-[#D4AF37]" : ""}`}
            style={p.badge ? { borderWidth: 1 } : {}}>
            {p.badge && <span className="self-start text-[10px] uppercase tracking-wider gold-gradient text-black px-2 py-0.5 rounded-full mb-2">{p.badge}</span>}
            <Sparkles className="text-[#D4AF37] mb-2" size={20} />
            <div className="font-heading text-3xl gold-text">{p.coins.toLocaleString()}</div>
            <div className="text-xs text-[#A0A0A0] mb-1">Empire Coins</div>
            <div className="text-sm text-white mb-4">${p.price}</div>
            <button data-testid={`buy-${p.id}`} onClick={() => buy(p)} disabled={buying === p.id}
              className="btn-gold rounded-full py-2 text-sm mt-auto flex items-center justify-center gap-2 disabled:opacity-60">
              {buying === p.id ? <Loader2 size={15} className="animate-spin" /> : null} {buying === p.id ? "Redirecting…" : "Buy with card"}
            </button>
            {Number(p.price) >= CRYPTO_MIN_USD ? (
              <>
                <button data-testid={`buy-crypto-${p.id}`} onClick={() => buyCrypto(p)} disabled={buyingCrypto === p.id}
                  className="btn-outline-gold rounded-full py-2 text-xs mt-2 flex items-center justify-center gap-2 disabled:opacity-60">
                  {buyingCrypto === p.id ? <Loader2 size={14} className="animate-spin" /> : <Bitcoin size={14} />} {buyingCrypto === p.id ? "Opening…" : "Pay with crypto"}
                </button>
                <div className="text-[10px] text-[#2ecc71] mt-1.5 text-center font-medium">+{(meta.cryptoPct || 10) + (meta.first ? (meta.firstPct || 20) : 0)}% bonus coins with crypto 🎁</div>
              </>
            ) : (
              <div className="text-[11px] text-[#8A8A8A] mt-2 py-2 text-center">Card only</div>
            )}
          </motion.div>
        ))}
      </div>
      <div className="text-xs text-[#666] mt-6 flex items-center gap-2"><Check size={13} className="text-[#D4AF37]" /> Secure checkout. Coins are non-refundable and have no cash value.</div>

      {/* Purchase history */}
      <div className="mt-10" data-testid="wallet-history">
        <h3 className="font-heading text-2xl mb-4">Purchase history</h3>
        {orders.length === 0 ? (
          <div className="pe-card rounded-2xl p-6 text-center text-sm text-[#8A8A8A]" data-testid="wallet-history-empty">No purchases yet — your coin top-ups will show up here.</div>
        ) : (
          <div className="pe-card rounded-2xl overflow-hidden divide-y divide-[#2A2A2A]">
            {orders.map((o, i) => (
              <div key={i} data-testid={`order-row-${i}`} className="flex items-center gap-4 p-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center bg-[#D4AF37]/12 border border-[#D4AF37]/30 flex-shrink-0">
                  {o.method === "crypto" ? <Bitcoin size={16} className="text-[#D4AF37]" /> : <Coins size={16} className="text-[#D4AF37]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white">{o.coins.toLocaleString()} coins <span className="text-[#8A8A8A]">· ${Number(o.amount).toFixed(2)}</span></div>
                  <div className="text-[11px] text-[#8A8A8A]">{o.method === "crypto" ? "Crypto" : "Card"}{o.created_at ? ` · ${new Date(o.created_at).toLocaleDateString()}` : ""}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full flex-shrink-0 ${o.status === "paid" ? "bg-[#2ecc71]/15 text-[#2ecc71]" : "bg-[#D4AF37]/15 text-[#D4AF37]"}`}>
                  {o.status === "paid" ? "Paid" : "Pending"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
